import { matchPath, useLocation as useRouterLocation } from "react-router-dom";
import { useLocationDetail } from "./locations";

/**
 * The active location id, read from the URL.
 *
 * `LocationRoute` already documented the URL as the single source of truth for
 * which location is active, but it then copied the `:id` param into Redux so the
 * listener middleware had something to react to. With one query per location id
 * that copy has no purpose, so the URL is now the only place this lives.
 *
 * Derived with `matchPath` rather than `useParams` so it also works for
 * components rendered outside the `/locations/:id` route subtree — the sidebar
 * and the support dialog both need it.
 */
export const useActiveLocationId = (): number | undefined => {
  const { pathname } = useRouterLocation();
  const match =
    matchPath("/locations/:id/*", pathname) ?? matchPath("/locations/:id", pathname);
  if (!match) return undefined;
  const id = Number(match.params.id);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

/** The full record for whichever location the URL points at. */
export const useActiveLocation = () => useLocationDetail(useActiveLocationId());
